import {ChangeDetectionStrategy, Component, computed, inject} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {toSignal} from '@angular/core/rxjs-interop';
import {filter, map} from 'rxjs/operators';
import {TranslateModule} from '@ngx-translate/core';
import {TopbarService} from 'src/app/services/ui/topbar.service';
import {MenuStateService} from 'src/app/services/ui/menu-state.service';
import {LayoutStore} from 'src/app/services/ui/layout.store';
import {CurrentTenantStore} from 'src/app/services/tenant/current-tenant.store';
import {TenantAccountActivityStore} from 'src/app/services/tenant/tenant-account-activity.signal-store';
import {GlobalResearchShortcutService} from 'src/app/services/search/global-research-shortcut.service';

import {DropdownTriggerDirective} from 'src/app/modules/design-system/dropdown/dropdown-trigger.directive';
import {UserMenuCardComponent} from 'src/app/modules/global/user-menu-card/user-menu-card.component';
import {TrialBadgeComponent} from 'src/app/modules/home/topbar/trial-badge/trial-badge.component';
import {MembersActivityComponent} from 'src/app/modules/home/topbar/members-activity/members-activity.component';
import {
	ANNOUNCEMENT_URL,
	APPLICATIONS_URL,
	ARCHITECTURE_URL,
	CUSTOM_DASHBOARDS_URL,
	DATA_URL,
	DIAGRAMS_URL,
	EXTENSION_URL,
	FINANCE_URL,
	HOME_URL,
	INFRASTRUCTURE_NETWORKS_URL,
	INFRASTRUCTURE_ROUTERS_URL,
	INFRASTRUCTURE_SERVERS_URL,
	OPERATION_URL,
	ORGANIZATION_URL,
	PRISM_URL,
	SETTINGS_URL,
	TECHNOLOGY_URL,
	USAGE_URL,
	USERS_USERS_URL,
	DEVICES_URL,
	WORKSTATIONS_DASHBOARD_URL
} from 'src/app/models/home/navigation.model';
import {DisplayUserLogoComponent} from 'src/app/modules/design-system/display-user-logo/display-user-logo.component';
import {DropdownComponent} from 'src/app/modules/design-system/dropdown/dropdown.component';

/**
 * Sidebar section icon (assets/icons/menu/*.svg) shown in the page header,
 * resolved from the current URL — longest matching prefix wins, and URLs
 * without an entry (login, drawers over unknown roots…) simply show no icon.
 */
const HEADER_ICON_BY_URL: readonly [url: string, icon: string][] = [
	[HOME_URL, 'home'],
	[PRISM_URL, 'prism'],
	[OPERATION_URL, 'operations'],
	[FINANCE_URL, 'finance'],
	[USAGE_URL, 'usage'],
	[ARCHITECTURE_URL, 'architecture'],
	[TECHNOLOGY_URL, 'technologies'],
	[WORKSTATIONS_DASHBOARD_URL, 'workstations'],
	[CUSTOM_DASHBOARDS_URL, 'custom-dashboards'],
	[DIAGRAMS_URL, 'schemas'],
	[ANNOUNCEMENT_URL, 'announcements'],
	[ORGANIZATION_URL, 'organization'],
	[USERS_USERS_URL, 'users'],
	[DEVICES_URL, 'workstations'],
	[APPLICATIONS_URL, 'applications'],
	[DATA_URL, 'data'],
	[INFRASTRUCTURE_SERVERS_URL, 'servers'],
	[INFRASTRUCTURE_NETWORKS_URL, 'network'],
	[INFRASTRUCTURE_ROUTERS_URL, 'network'],
	[EXTENSION_URL, 'extension'],
	[SETTINGS_URL, 'settings']
];

@Component({
	selector: 'app-topbar',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [TranslateModule, DisplayUserLogoComponent, DropdownComponent, DropdownTriggerDirective, UserMenuCardComponent, TrialBadgeComponent, MembersActivityComponent],
	templateUrl: './topbar.component.html',
	styleUrls: ['./topbar.component.scss']
})
export class TopbarComponent {

	private topbarService = inject(TopbarService);
	private router = inject(Router);
	protected readonly layout = inject(LayoutStore);
	protected readonly menuState = inject(MenuStateService);
	protected readonly research = inject(GlobalResearchShortcutService);
	private tenantStore = inject(CurrentTenantStore);

	protected readonly suspended = this.tenantStore.isSuspended;

	/** Compte courant pour l'avatar de la barre téléphone. */
	protected readonly currentUser = this.tenantStore.currentAccount;

	protected readonly title = this.topbarService.title;
	protected readonly subtitle = this.topbarService.subtitle;
	protected readonly indicator = this.topbarService.indicator;

	private readonly currentUrl = toSignal(
		this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			map(() => this.router.url)
		),
		{initialValue: this.router.url}
	);

	protected readonly icon = computed(() => {
		const url = this.currentUrl();
		const [, icon] = HEADER_ICON_BY_URL
			.filter(([prefix]) => url.startsWith(prefix))
			.sort(([a], [b]) => b.length - a.length)[0] ?? [];
		return icon;
	});

	constructor() {
		// The header lives as long as the signed-in shell: it keeps the members' activity fresh, for the menu too (KAB-2397).
		inject(TenantAccountActivityStore).poll();
	}
}
